import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
  useEffect,
  LegacyRef,
} from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import styles from "./CreateAdvertisementModal.module.scss";
import PrimaryButton, {
  PrimaryButtonSize,
  PrimaryButtonType,
} from "../../common/PrimaryButton";
import ImageUpload from "../../common/hookForm/ImageUpload";
import Checkbox from "../../common/hookForm/Checkbox";
import { useAdvertisementsStore } from "../../../store/advertisements";
import { useAlertStore } from "../../../store/alertStore";
import {
  EAdvertisementPlatform,
  EAdvertisementStatus,
} from "../../../setup/api/services/advertisements/models";
import { uploadFile } from "../../../s3Integration/s3Client";
import { EUploadImageType } from "../../../s3Integration/enums";
import { getAdvertisementById } from "../../../setup/api/services/advertisements";
import { IApiResponse } from "../../../setup/api/models";

interface Props {
  title: string;
}

interface FormData {
  platforms: {
    ios: boolean;
    android: boolean;
    web: boolean;
  };
}

interface ImagePreview {
  id: string;
  url: string;
  file: File;
}

export interface CreateAdvertisementModalRef {
  openModal: (advertisementId?: string) => void;
  closeModal: () => void;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
];

const CreateAdvertisementModal = forwardRef<CreateAdvertisementModalRef, Props>(
  (props, ref) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState<boolean>(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [webImage, setWebImage] = useState<ImagePreview | null>(null);
    const [mobileImage, setMobileImage] = useState<ImagePreview | null>(null);
    const [webImageUrl, setWebImageUrl] = useState<string>("");
    const [mobileImageUrl, setMobileImageUrl] = useState<string>("");
    const [uploading, setUploading] = useState<boolean>(false);
    const [showWebImageError, setShowWebImageError] = useState<boolean>(false);
    const [showMobileImageError, setShowMobileImageError] =
      useState<boolean>(false);
    const [showPlatformError, setShowPlatformError] = useState<boolean>(false);

    const createAdvertisement = useAdvertisementsStore(
      (s) => s.createAdvertisement
    );
    const updateAdvertisement = useAdvertisementsStore(
      (s) => s.updateAdvertisement
    );
    const fetchAdvertisements = useAdvertisementsStore(
      (s) => s.fetchAdvertisements
    );
    const { showError, showSuccess } = useAlertStore();

    const isEditMode = !!editingId;

    // React Hook Form setup
    const { control, reset, handleSubmit } = useForm<FormData>({
      defaultValues: {
        platforms: {
          ios: false,
          android: false,
          web: false,
        },
      },
    });

    const openModal = useCallback((advertisementId?: string) => {
      setOpen(true);
      if (advertisementId) {
        setEditingId(advertisementId);
      }
    }, []);

    const closeModal = useCallback(() => {
      setOpen(false);
      setEditingId(null);
      setWebImage(null);
      setMobileImage(null);
      setWebImageUrl("");
      setMobileImageUrl("");
      setShowWebImageError(false);
      setShowMobileImageError(false);
      setShowPlatformError(false);
      reset();
    }, [reset]);

    // Fetch advertisement data when opening in edit mode
    useEffect(() => {
      const fetchAdvertisementData = async () => {
        if (!editingId || !open) return;

        try {
          setLoading(true);
          const advertisement = await getAdvertisementById(editingId);

          // Set image URLs
          setWebImageUrl(advertisement.webImage);
          setMobileImageUrl(advertisement.mobileImage);

          // Set platforms
          reset({
            platforms: {
              ios: advertisement.surfaces.includes(EAdvertisementPlatform.IOS),
              android: advertisement.surfaces.includes(
                EAdvertisementPlatform.ANDROID
              ),
              web: advertisement.surfaces.includes(EAdvertisementPlatform.WEB),
            },
          });
        } catch (error) {
          console.error("Error fetching advertisement:", error);
          showError(t("advertisement_fetch_error"));
          closeModal();
        } finally {
          setLoading(false);
        }
      };

      fetchAdvertisementData();
    }, [editingId, open, reset, showError, t, closeModal]);

    const stopPropagation = useCallback((event: React.MouseEvent) => {
      event.stopPropagation();
    }, []);

    const validateImage = useCallback(
      (file: File): string | null => {
        // Check file size
        if (file.size > MAX_FILE_SIZE) {
          return t("advertisement_image_size_error");
        }

        // Check file type
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
          return t("advertisement_image_type_error");
        }

        return null;
      },
      [t]
    );

    const handleWebImageChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
          // Validate image
          const error = validateImage(file);
          if (error) {
            showError(error);
            e.target.value = ""; // Reset input
            return;
          }

          const reader = new FileReader();
          reader.onloadend = () => {
            const newImage: ImagePreview = {
              id: Math.random().toString(36).substr(2, 9),
              url: reader.result as string,
              file: file,
            };
            // Replace existing image
            setWebImage(newImage);
            setShowWebImageError(false); // Clear error when image is selected
          };
          reader.readAsDataURL(file);
        }
        e.target.value = ""; // Reset input to allow re-selecting same file
      },
      [validateImage, showError]
    );

    const handleMobileImageChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
          // Validate image
          const error = validateImage(file);
          if (error) {
            showError(error);
            e.target.value = ""; // Reset input
            return;
          }

          const reader = new FileReader();
          reader.onloadend = () => {
            const newImage: ImagePreview = {
              id: Math.random().toString(36).substr(2, 9),
              url: reader.result as string,
              file: file,
            };
            // Replace existing image
            setMobileImage(newImage);
            setShowMobileImageError(false); // Clear error when image is selected
          };
          reader.readAsDataURL(file);
        }
        e.target.value = ""; // Reset input to allow re-selecting same file
      },
      [validateImage, showError]
    );

    const removeWebImage = useCallback(() => {
      setWebImage(null);
      setWebImageUrl("");
    }, []);

    const removeMobileImage = useCallback(() => {
      setMobileImage(null);
      setMobileImageUrl("");
    }, []);

    const handlePlatformChange = useCallback(() => {
      // Clear platform error when any platform is selected
      setShowPlatformError(false);
    }, []);

    const onSubmit = async (data: FormData) => {
      try {
        // Reset all error states
        setShowWebImageError(false);
        setShowMobileImageError(false);
        setShowPlatformError(false);

        // Validate that both images are present (uploaded or existing URLs)
        let hasErrors = false;

        if (!webImage && !webImageUrl) {
          setShowWebImageError(true);
          hasErrors = true;
        }

        if (!mobileImage && !mobileImageUrl) {
          setShowMobileImageError(true);
          hasErrors = true;
        }

        // Validate that at least one platform is selected
        const selectedPlatforms = Object.entries(data.platforms)
          .filter(([, selected]) => selected)
          .map(([platform]) => {
            switch (platform) {
              case "ios":
                return EAdvertisementPlatform.IOS;
              case "android":
                return EAdvertisementPlatform.ANDROID;
              case "web":
                return EAdvertisementPlatform.WEB;
              default:
                return null;
            }
          })
          .filter((p): p is EAdvertisementPlatform => p !== null);

        if (selectedPlatforms.length === 0) {
          setShowPlatformError(true);
          hasErrors = true;
        }

        // If there are validation errors, stop here
        if (hasErrors) {
          return;
        }

        setUploading(true);

        // Upload new images to S3 or use existing URLs
        let finalWebImageUrl = webImageUrl;
        let finalMobileImageUrl = mobileImageUrl;

        if (webImage) {
          finalWebImageUrl = await uploadFile(
            webImage.file,
            EUploadImageType.PROFILE,
            "promotions"
          );
        }

        if (mobileImage) {
          finalMobileImageUrl = await uploadFile(
            mobileImage.file,
            EUploadImageType.PROFILE,
            "promotions"
          );
        }

        // Create or update advertisement
        if (isEditMode && editingId) {
          // Update existing advertisement
          const result = await updateAdvertisement(editingId, {
            id: editingId,
            webImage: finalWebImageUrl,
            mobileImage: finalMobileImageUrl,
            surfaces: selectedPlatforms,
            status: EAdvertisementStatus.ACTIVE,
          });

          if (result) {
            showSuccess(t("advertisement_update_success"));
            await fetchAdvertisements();
            closeModal();
          } else {
            showError(t("advertisement_update_error"));
          }
        } else {
          // Create new advertisement
          const result = await createAdvertisement({
            webImage: finalWebImageUrl,
            mobileImage: finalMobileImageUrl,
            surfaces: selectedPlatforms,
            status: EAdvertisementStatus.ACTIVE,
          });

          if (result) {
            showSuccess(t("advertisement_create_success"));
            await fetchAdvertisements();
            closeModal();
          } else {
            showError(t("advertisement_create_error"));
          }
        }
      } catch (error) {
        console.error("Error saving advertisement:", error);
        const errorMessage = (error as IApiResponse)?.error?.message;
        if (
          errorMessage &&
          errorMessage.toLowerCase().includes("count of active")
        ) {
          showError(t("advertisement_image_count_error"));
        } else {
          const errorKey = isEditMode
            ? "advertisement_update_error_occurred"
            : "advertisement_create_error_occurred";
          showError(t(errorKey));
        }
      } finally {
        setUploading(false);
      }
    };

    useImperativeHandle(ref, () => ({
      openModal,
      closeModal,
    }));

    if (!open) return null;

    return (
      <div ref={ref as LegacyRef<HTMLDivElement>} className={styles.modal}>
        <div onClick={closeModal} className={styles.mask} />
        <div onClick={stopPropagation} className={styles.content}>
          {/* Header */}
          <div className={styles.header}>
            <h2 className={styles.title}>
              {isEditMode
                ? t("advertisement_edit_title")
                : t("advertisement_add_title")}
            </h2>
            <div onClick={closeModal} className={styles.close_button}>
              <img src={"/assets/images/svg/cross_icon.svg"} alt="menu" />
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            {/* Image Upload Section */}
            <div className={styles.images_section}>
              {/* Web Images */}
              <div className={styles.image_upload_container}>
                <label className={styles.label}>
                  {t("advertisement_web_image")}
                </label>

                <div className={styles.images_scroll_container}>
                  {!webImage && !webImageUrl && (
                    <div className={styles.upload_box}>
                      <ImageUpload
                        handleChange={handleWebImageChange}
                        multiple={false}
                      />
                    </div>
                  )}
                  {(webImage || webImageUrl) && (
                    <div className={styles.image_preview_box}>
                      <img
                        src={webImage?.url || webImageUrl}
                        alt="Web preview"
                        className={styles.preview_image}
                      />
                      <button
                        type="button"
                        className={styles.remove_image_btn}
                        onClick={removeWebImage}>
                        ✕
                      </button>
                    </div>
                  )}
                </div>
                {showWebImageError && (
                  <p className={styles.error_message}>
                    {t("advertisement_web_image_required")}
                  </p>
                )}
              </div>

              {/* Mobile Images */}
              <div className={styles.image_upload_container}>
                <label className={styles.label}>
                  {t("advertisement_mobile_image")}
                </label>

                <div className={styles.images_scroll_container}>
                  {!mobileImage && !mobileImageUrl && (
                    <div className={styles.upload_box}>
                      <ImageUpload
                        handleChange={handleMobileImageChange}
                        multiple={false}
                      />
                    </div>
                  )}
                  {(mobileImage || mobileImageUrl) && (
                    <div className={styles.image_preview_box}>
                      <img
                        src={mobileImage?.url || mobileImageUrl}
                        alt="Mobile preview"
                        className={styles.preview_image}
                      />
                      <button
                        type="button"
                        className={styles.remove_image_btn}
                        onClick={removeMobileImage}>
                        ✕
                      </button>
                    </div>
                  )}
                </div>
                {showMobileImageError && (
                  <p className={styles.error_message}>
                    {t("advertisement_mobile_image_required")}
                  </p>
                )}
              </div>
            </div>

            {/* Platforms Section */}
            <div className={styles.platforms_section}>
              <label className={styles.section_label}>
                {t("advertisement_platforms")}
              </label>
              <div className={styles.checkboxes_container}>
                <Checkbox
                  name="platforms.ios"
                  control={control}
                  label="iOS"
                  onChange={handlePlatformChange}
                />
                <Checkbox
                  name="platforms.android"
                  control={control}
                  label="Android"
                  onChange={handlePlatformChange}
                />
                <Checkbox
                  name="platforms.web"
                  control={control}
                  label="Web"
                  onChange={handlePlatformChange}
                />
              </div>
              {showPlatformError && (
                <p className={styles.error_message}>
                  {t("advertisement_platform_required")}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <div className={styles.button_container}>
              <PrimaryButton
                type="submit"
                size={PrimaryButtonSize.REGULAR}
                buttonType={
                  uploading || loading
                    ? PrimaryButtonType.DISABLED
                    : PrimaryButtonType.PRIMARY_DARK
                }
                backgroundColor="primary"
                disabled={uploading || loading}>
                <p className={styles.button_text}>
                  {uploading
                    ? t("lbl_loading")
                    : isEditMode
                    ? t("advertisement_update")
                    : t("advertisement_add")}
                </p>
              </PrimaryButton>
            </div>
          </form>
        </div>
      </div>
    );
  }
);

CreateAdvertisementModal.displayName = "CreateAdvertisementModal";

export default CreateAdvertisementModal;
